import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { searchCards, getTypes, getRarities } from '../services/pokemonApi';
import { searchGameCards, SUPPORTED_GAMES, getCardImageUrl, getCardName, getCardRarity } from '../services/tcgApi';
import '../styles/CardsPage.css';

const SearchCard = ({ card, gameType }) => {
  const cardRef = useRef(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [glarePosition, setGlarePosition] = useState({ x: 50, y: 50 });
  const [showModal, setShowModal] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const rotateX = (mouseY / (rect.height / 2)) * -12;
    const rotateY = (mouseX / (rect.width / 2)) * 12;

    const glareX = ((e.clientX - rect.left) / rect.width) * 100;
    const glareY = ((e.clientY - rect.top) / rect.height) * 100;

    setTransform({ rotateX, rotateY });
    setGlarePosition({ x: glareX, y: glareY });
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setTransform({ rotateX: 0, rotateY: 0 });
    setGlarePosition({ x: 50, y: 50 });
  };

  // Get image URL based on game type
  const getImageUrl = () => {
    if (gameType === 'pokemon') {
      return card.images?.small || card.images?.large;
    }
    return getCardImageUrl(card, gameType);
  };

  // Get card display name
  const getDisplayName = () => {
    if (gameType === 'pokemon') return card.name;
    return getCardName(card);
  };

  // Get card set/series name
  const getSetName = () => {
    if (gameType === 'pokemon') return card.set?.name || 'Unknown Set';
    // Handle Scryfall (Magic) set_name
    if (card.set_name) return card.set_name;
    // Handle Star Wars (capital S)
    if (card.Set) return card.Set;
    // Handle Yu-Gi-Oh card_sets array
    if (card.card_sets && card.card_sets[0]) {
      return card.card_sets[0].set_name || 'Unknown Set';
    }
    // Handle set as object or string
    if (card.set) {
      return typeof card.set === 'object' ? card.set.name : card.set;
    }
    return card.series || card.cardSet || card.expansion || 'Unknown Set';
  };

  // Get rarity
  const getRarityValue = () => {
    if (gameType === 'pokemon') return card.rarity || 'Common';
    return getCardRarity(card);
  };

  // Get game color
  const getGameColor = () => {
    const game = SUPPORTED_GAMES.find(g => g.id === gameType || g.endpoint === gameType);
    return game?.color || '#ff1a1a';
  };

  const imageUrl = getImageUrl();
  const hasImage = imageUrl && !imageError;

  return (
    <>
      <div
        ref={cardRef}
        className={`search-card ${isHovered ? 'hovered' : ''} game-${gameType}`}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setShowModal(true)}
        style={{
          transform: `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) ${isHovered ? 'scale(1.05)' : 'scale(1)'}`,
        }}
      >
        <div className="search-card-frame" style={{ '--game-color': getGameColor() }}>
          {/* Holographic Effect */}
          <div
            className="card-holographic"
            style={{
              background: `radial-gradient(circle at ${glarePosition.x}% ${glarePosition.y}%,
                rgba(255,255,255,0.8) 0%,
                rgba(255,200,200,0.4) 20%,
                rgba(255,100,100,0.2) 40%,
                transparent 60%)`,
              opacity: isHovered ? 1 : 0,
            }}
          />

          {/* Rainbow Shine */}
          <div
            className="card-rainbow"
            style={{
              background: `linear-gradient(
                ${135 + transform.rotateY}deg,
                rgba(255,0,0,0.25) 0%,
                rgba(255,127,0,0.25) 14%,
                rgba(255,255,0,0.25) 28%,
                rgba(0,255,0,0.25) 42%,
                rgba(0,0,255,0.25) 57%,
                rgba(75,0,130,0.25) 71%,
                rgba(148,0,211,0.25) 85%,
                rgba(255,0,0,0.25) 100%
              )`,
              opacity: isHovered ? 0.7 : 0,
            }}
          />

          {/* Card Image */}
          {hasImage ? (
            <div className="search-card-image-wrapper">
              {!imageLoaded && (
                <div className="card-loading-placeholder">
                  <div className="loading-spinner-small"></div>
                </div>
              )}
              <img
                src={imageUrl}
                alt={getDisplayName()}
                className={`search-card-image ${imageLoaded ? 'loaded' : ''}`}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div className="search-card-placeholder" style={{ background: getGameColor() }}>
              <span className="placeholder-text">{getDisplayName()}</span>
            </div>
          )}

          {/* Sparkles */}
          <div className="card-sparkles">
            <span className="sparkle s1"></span>
            <span className="sparkle s2"></span>
            <span className="sparkle s3"></span>
            <span className="sparkle s4"></span>
          </div>

          {/* Border Glow */}
          <div className="card-border-glow"></div>
        </div>

        {/* Card Info */}
        <div className="search-card-info">
          <h3 className="search-card-name">{getDisplayName()}</h3>
          <p className="search-card-set">{getSetName()}</p>
          <div className="search-card-meta">
            <span className="card-game-badge" style={{ background: getGameColor() }}>
              {gameType.replace('-', ' ').toUpperCase()}
            </span>
            <span className="card-rarity-badge">{getRarityValue()}</span>
          </div>
        </div>
      </div>

      {/* Card Detail Modal */}
      {showModal && (
        <div className="card-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="card-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              &times;
            </button>
            <div className="modal-content">
              <div className="modal-image">
                {hasImage ? (
                  <img
                    src={gameType === 'pokemon' ? (card.images?.large || card.images?.small) : imageUrl}
                    alt={getDisplayName()}
                  />
                ) : (
                  <div className="modal-placeholder" style={{ background: getGameColor() }}>
                    <span>{getDisplayName()}</span>
                  </div>
                )}
              </div>
              <div className="modal-details">
                <div className="modal-game-tag" style={{ background: getGameColor() }}>
                  {gameType.replace('-', ' ').toUpperCase()}
                </div>
                <h2 className="modal-title">{getDisplayName()}</h2>
                <div className="modal-info-grid">
                  <div className="modal-info-item">
                    <span className="info-label">Set</span>
                    <span className="info-value">{getSetName()}</span>
                  </div>
                  {(card.number || card.code) && (
                    <div className="modal-info-item">
                      <span className="info-label">Number</span>
                      <span className="info-value">{card.number || card.code}</span>
                    </div>
                  )}
                  <div className="modal-info-item">
                    <span className="info-label">Rarity</span>
                    <span className="info-value">{getRarityValue()}</span>
                  </div>
                  {(card.hp || card.power) && (
                    <div className="modal-info-item">
                      <span className="info-label">{card.hp ? 'HP' : 'Power'}</span>
                      <span className="info-value">{card.hp || card.power}</span>
                    </div>
                  )}
                  {/* Yu-Gi-Oh ATK/DEF */}
                  {(card.atk !== undefined || card.def !== undefined) && (
                    <div className="modal-info-item">
                      <span className="info-label">ATK / DEF</span>
                      <span className="info-value">{card.atk ?? '?'} / {card.def ?? '?'}</span>
                    </div>
                  )}
                  {/* Yu-Gi-Oh Level/Rank */}
                  {(card.level || card.linkval) && (
                    <div className="modal-info-item">
                      <span className="info-label">{card.linkval ? 'Link' : 'Level'}</span>
                      <span className="info-value">{card.level || card.linkval}</span>
                    </div>
                  )}
                  {(card.types || card.type || card.color || card.race) && (
                    <div className="modal-info-item">
                      <span className="info-label">Type</span>
                      <span className="info-value">
                        {card.types?.join(', ') || card.race || card.type || card.color}
                      </span>
                    </div>
                  )}
                  {(card.artist || card.illustrator) && (
                    <div className="modal-info-item">
                      <span className="info-label">Artist</span>
                      <span className="info-value">{card.artist || card.illustrator}</span>
                    </div>
                  )}
                  {card.cost && (
                    <div className="modal-info-item">
                      <span className="info-label">Cost</span>
                      <span className="info-value">{card.cost}</span>
                    </div>
                  )}
                  {card.counter && (
                    <div className="modal-info-item">
                      <span className="info-label">Counter</span>
                      <span className="info-value">{card.counter}</span>
                    </div>
                  )}
                </div>

                {/* Pokemon Attacks */}
                {card.attacks && card.attacks.length > 0 && (
                  <div className="modal-attacks">
                    <h3>Attacks</h3>
                    {card.attacks.map((attack, index) => (
                      <div key={index} className="attack-item">
                        <div className="attack-header">
                          <span className="attack-name">{attack.name}</span>
                          <span className="attack-damage">{attack.damage}</span>
                        </div>
                        {attack.text && <p className="attack-text">{attack.text}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Ability/Effect Text */}
                {(card.ability || card.effect || card.text || card.desc) && (
                  <div className="modal-ability">
                    <h3>Ability / Effect</h3>
                    <p>{card.ability || card.effect || card.text || card.desc}</p>
                  </div>
                )}

                {/* Pokemon Prices */}
                {card.tcgplayer?.prices && (
                  <div className="modal-prices">
                    <h3>Market Prices</h3>
                    <div className="price-grid">
                      {Object.entries(card.tcgplayer.prices).map(([type, prices]) => (
                        <div key={type} className="price-item">
                          <span className="price-type">{type}</span>
                          <span className="price-value">
                            ${prices.market?.toFixed(2) || prices.mid?.toFixed(2) || 'N/A'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Yu-Gi-Oh Prices */}
                {card.card_prices && card.card_prices[0] && (
                  <div className="modal-prices">
                    <h3>Market Prices</h3>
                    <div className="price-grid">
                      {card.card_prices[0].tcgplayer_price && (
                        <div className="price-item">
                          <span className="price-type">TCGPlayer</span>
                          <span className="price-value">${card.card_prices[0].tcgplayer_price}</span>
                        </div>
                      )}
                      {card.card_prices[0].cardmarket_price && (
                        <div className="price-item">
                          <span className="price-type">Cardmarket</span>
                          <span className="price-value">${card.card_prices[0].cardmarket_price}</span>
                        </div>
                      )}
                      {card.card_prices[0].amazon_price && card.card_prices[0].amazon_price !== '0.00' && (
                        <div className="price-item">
                          <span className="price-type">Amazon</span>
                          <span className="price-value">${card.card_prices[0].amazon_price}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CardsPage = () => {
  const [searchParams] = useSearchParams();
  const gameFromUrl = searchParams.get('game');

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGame, setSelectedGame] = useState(gameFromUrl || 'pokemon');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Pokemon-specific filters
  const [types, setTypes] = useState([]);
  const [rarities, setRarities] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedRarity, setSelectedRarity] = useState('');

  const pageSize = 20;

  // Ref to track current request and prevent race conditions
  const currentRequestRef = useRef(0);

  // Update selected game when URL param changes
  useEffect(() => {
    if (gameFromUrl && SUPPORTED_GAMES.some(g => g.id === gameFromUrl)) {
      setSelectedGame(gameFromUrl);
    }
  }, [gameFromUrl]);

  // Fetch Pokemon filter options
  useEffect(() => {
    if (selectedGame === 'pokemon') {
      const fetchFilters = async () => {
        try {
          const [typesData, raritiesData] = await Promise.all([
            getTypes(),
            getRarities()
          ]);
          setTypes(typesData.data || []);
          setRarities(raritiesData.data || []);
        } catch (err) {
          console.error('Failed to fetch filters:', err);
        }
      };
      fetchFilters();
    }
  }, [selectedGame]);

  // Build Pokemon query string
  const buildPokemonQuery = useCallback(() => {
    const parts = [];
    if (searchQuery) {
      parts.push(`name:*${searchQuery}*`);
    }
    if (selectedType) {
      parts.push(`types:${selectedType}`);
    }
    if (selectedRarity) {
      parts.push(`rarity:"${selectedRarity}"`);
    }
    return parts.join(' ');
  }, [searchQuery, selectedType, selectedRarity]);

  // Fetch cards
  const fetchCards = useCallback(async (resetPage = false) => {
    // Increment request ID to track this specific request
    const requestId = ++currentRequestRef.current;

    try {
      setLoading(true);
      setError(null);
      const currentPage = resetPage ? 1 : page;

      let response;

      if (selectedGame === 'pokemon') {
        // Use Pokemon TCG API
        const query = buildPokemonQuery();
        response = await searchCards(query, currentPage, pageSize);

        // Check if this request is still the current one
        if (requestId !== currentRequestRef.current) return;

        setCards(response.data || []);
        setTotalCount(response.totalCount || 0);
      } else {
        // Use Api TCG for other games
        const game = SUPPORTED_GAMES.find(g => g.id === selectedGame);
        if (game) {
          const searchParams = {};
          if (searchQuery) searchParams.name = searchQuery;
          if (selectedRarity) searchParams.rarity = selectedRarity;

          response = await searchGameCards(game.endpoint, searchParams);

          // Check if this request is still the current one
          if (requestId !== currentRequestRef.current) return;

          // Handle different response structures
          const cardData = response.cards || response.data || response || [];
          let cardsArray = Array.isArray(cardData) ? cardData : [];

          // Client-side rarity filter as fallback
          if (selectedRarity && cardsArray.length > 0) {
            cardsArray = cardsArray.filter(card => {
              const cardRarity = (card.rarity || '').toUpperCase();
              return cardRarity.includes(selectedRarity.toUpperCase());
            });
          }

          // Client-side pagination for non-Pokemon APIs
          const startIndex = (currentPage - 1) * pageSize;
          const paginatedCards = cardsArray.slice(startIndex, startIndex + pageSize);

          setCards(paginatedCards);
          setTotalCount(cardsArray.length);
        }
      }

      if (resetPage) setPage(1);
    } catch (err) {
      // Only show error if this is still the current request
      if (requestId !== currentRequestRef.current) return;
      setError('Failed to fetch cards. Please try again.');
      console.error(err);
    } finally {
      // Only update loading if this is still the current request
      if (requestId === currentRequestRef.current) {
        setLoading(false);
      }
    }
  }, [page, selectedGame, searchQuery, selectedType, selectedRarity, buildPokemonQuery]);

  // Reset and fetch when game or filters change
  useEffect(() => {
    setSelectedType('');
    setSelectedRarity('');
    fetchCards(true);
  }, [selectedGame]);

  // Fetch when search or filters change
  useEffect(() => {
    fetchCards(true);
  }, [searchQuery, selectedType, selectedRarity]);

  // Fetch when page changes
  useEffect(() => {
    if (page > 1) {
      fetchCards(false);
    }
  }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleGameChange = (gameId) => {
    setSelectedGame(gameId);
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setSelectedType('');
    setSelectedRarity('');
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const currentGame = SUPPORTED_GAMES.find(g => g.id === selectedGame);

  return (
    <div className="cards-page">
      <div className="cards-page-header">
        <div className="comic-panel-border">
          <h1 className="page-title">
            <span className="title-text">CARDSLAH DATABASE</span>
            <span className="title-burst">!</span>
          </h1>
          <p className="page-subtitle">
            Malaysia's Premier Hub for Trading Card Enthusiasts
          </p>
          <p className="page-description">
            Search cards from Pokemon, One Piece, Dragon Ball, Digimon, Magic: The Gathering,
            Star Wars, Gundam & more! Built by collectors, for collectors.
          </p>
          <div className="cardslah-social">
            <a href="https://instagram.com/cardslah" target="_blank" rel="noopener noreferrer">@cardslah</a>
            <span className="social-divider">|</span>
            <a href="https://esportslah.gg/#cardslah" target="_blank" rel="noopener noreferrer">esportslah.gg</a>
          </div>
        </div>
      </div>

      {/* Game Selector */}
      <div className="game-selector">
        <p className="selector-label">SELECT GAME:</p>
        <div className="game-tabs">
          {SUPPORTED_GAMES.map((game) => (
            <button
              key={game.id}
              className={`game-tab ${selectedGame === game.id ? 'active' : ''}`}
              onClick={() => handleGameChange(game.id)}
              style={{
                '--game-color': game.color,
                borderColor: selectedGame === game.id ? game.color : undefined,
                background: selectedGame === game.id ? game.color : undefined
              }}
            >
              {game.name}
            </button>
          ))}
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={`Search ${currentGame?.name || 'Pokemon'} cards by name...`}
              className="search-input"
            />
            <button type="submit" className="search-button" style={{ background: currentGame?.color }}>
              <span className="btn-text">SEARCH</span>
              <span className="btn-burst">!</span>
            </button>
          </div>
        </form>

        {/* Filters for all games */}
        <div className="filters-section">
          {selectedGame === 'pokemon' && (
            <div className="filter-group">
              <label className="filter-label">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                {types.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label className="filter-label">Rarity</label>
            <select
              value={selectedRarity}
              onChange={(e) => setSelectedRarity(e.target.value)}
              className="filter-select"
            >
              <option value="">All Rarities</option>
              {selectedGame === 'pokemon' ? (
                rarities.map((rarity) => (
                  <option key={rarity} value={rarity}>{rarity}</option>
                ))
              ) : selectedGame === 'magic' ? (
                <>
                  <option value="common">Common</option>
                  <option value="uncommon">Uncommon</option>
                  <option value="rare">Rare</option>
                  <option value="mythic">Mythic Rare</option>
                </>
              ) : selectedGame === 'star-wars' ? (
                <>
                  <option value="Common">Common</option>
                  <option value="Uncommon">Uncommon</option>
                  <option value="Rare">Rare</option>
                  <option value="Legendary">Legendary</option>
                  <option value="Special">Special</option>
                </>
              ) : selectedGame === 'yugioh' ? (
                <>
                  <option value="Common">Common</option>
                  <option value="Rare">Rare</option>
                  <option value="Super Rare">Super Rare</option>
                  <option value="Ultra Rare">Ultra Rare</option>
                  <option value="Secret Rare">Secret Rare</option>
                  <option value="Ultimate Rare">Ultimate Rare</option>
                  <option value="Ghost Rare">Ghost Rare</option>
                  <option value="Starlight Rare">Starlight Rare</option>
                </>
              ) : (
                <>
                  <option value="C">Common</option>
                  <option value="U">Uncommon</option>
                  <option value="R">Rare</option>
                  <option value="SR">Super Rare</option>
                  <option value="L">Leader</option>
                  <option value="SEC">Secret Rare</option>
                  <option value="P">Promo</option>
                </>
              )}
            </select>
          </div>

          <button onClick={handleClearFilters} className="clear-filters-btn">
            Clear Filters
          </button>
        </div>

        {/* Results Info */}
        <div className="results-info">
          <span className="results-count" style={{ color: currentGame?.color }}>
            {totalCount.toLocaleString()} {currentGame?.name} cards found
          </span>
          {searchQuery && (
            <span className="active-filters">
              <span className="filter-tag" style={{ background: currentGame?.color }}>
                Search: {searchQuery}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <img
            src="/images/cardslah-logo.png"
            alt="Loading"
            className="loading-logo"
          />
          <p className="loading-text">Shuffling {currentGame?.name} cards...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={() => fetchCards(true)} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && !error && (
        <>
          <div className="search-cards-grid">
            {cards.map((card, index) => (
              <SearchCard
                key={card.id || `${selectedGame}-${index}`}
                card={card}
                gameType={selectedGame}
              />
            ))}
          </div>

          {cards.length === 0 && (
            <div className="no-results">
              <h3>No cards found</h3>
              <p>Try adjusting your search or selecting a different game</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn"
                style={{ '--game-color': currentGame?.color }}
              >
                Previous
              </button>

              <div className="pagination-info">
                <span>Page {page} of {totalPages}</span>
              </div>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="pagination-btn"
                style={{ '--game-color': currentGame?.color }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Page Decoration */}
      <div className="page-decoration">
        <div className="speed-lines left"></div>
        <div className="speed-lines right"></div>
      </div>
    </div>
  );
};

export default CardsPage;
